import React from "react";
import { Button, Space, Tag, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";

// 列表面板配置
export interface TablePanelConfig {
  title: string;
  storageKey: string;
  resizableColumns: boolean;
  saveColumnWidths: boolean;
}

export const tablePanelConfig: TablePanelConfig = {
  title: "角色列表",
  storageKey: "role_management_table",
  resizableColumns: true,
  saveColumnWidths: true,
};

// 将表头按钮（业务按钮数组）映射为 ContainerPanel 的 ButtonConfig[]
export const getTableHeaderButtons = (
  tableButtons: Array<{ key: string; text: string; type?: any; icon?: React.ReactElement; onClick: () => void }>
) =>
  tableButtons.map((btn) => ({
    key: btn.key,
    text: btn.text,
    type: btn.type,
    icon: btn.icon,
    onClick: btn.onClick,
    group: "primary" as const,
  }));

export interface TableButtonConfig {
  key: string;
  icon?: React.ReactElement;
  onClick: () => void;
  text: string;
  type?: "primary" | "danger-border" | "danger" | "default" | "link" | "dashed" | "text";
  group?: "primary" | "secondary" | "tertiary";
  disabled?: boolean;
}

export interface Role {
  id: string;
  name: string;
  code: string;
  description?: string;
  permissions?: any[];
  adminCount?: number;
  permissionCount?: number | undefined;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  isSystem?: boolean;
  _id?: string;
}

export const createRoleColumns = (
  onEdit: (record: Role) => void,
  onToggleStatus: (record: Role) => void,
  onAssignAdmins: (record: Role) => void,
  onDelete: (record: Role) => void,
  onViewPermissions: (record: Role) => void
) => [
  {
    title: "角色名称",
    dataIndex: "name",
    key: "name",
    width: 180,
    minWidth: 120,
  },
  {
    title: "角色编码",
    dataIndex: "code",
    key: "code",
    width: 150,
    minWidth: 100,
    render: (text: string) =>
      text && text.trim() ? <Tag color="blue">{text}</Tag> : <span style={{ color: "#999" }}>未设置</span>,
  },
  {
    title: "描述",
    dataIndex: "description",
    key: "description",
    width: 200,
    minWidth: 150,
    ellipsis: { showTitle: false },
    render: (description: string) => <Tooltip title={description}>{description || "-"}</Tooltip>,
  },
  {
    title: "状态",
    dataIndex: "status",
    key: "status",
    width: 100,
    minWidth: 80,
    render: (status: string, record: Role) => {
      if (!status) return <Tag color="default">未设置</Tag>;
      const isActive = status === "active";
      return (
        <Tag
          color={isActive ? "success" : "default"}
          style={{ cursor: "pointer" }}
          title={`点击${isActive ? "禁用" : "启用"}角色`}
          onClick={() => onToggleStatus(record)}
        >
          {isActive ? "启用" : "禁用"}
        </Tag>
      );
    },
  },
  {
    title: "管理员数量",
    key: "adminCount",
    width: 120,
    minWidth: 100,
    render: (_: any, record: Role) => {
      const count = record.adminCount || 0;
      return (
        <Tag
          color={count > 0 ? "blue" : "default"}
          style={{ cursor: "pointer" }}
          title="点击管理管理员"
          onClick={() => onAssignAdmins(record)}
        >
          {count}
        </Tag>
      );
    },
  },
  {
    title: "权限数量",
    dataIndex: "permissionCount",
    key: "permissionCount",
    width: 120,
    minWidth: 100,
    render: (count: number, record: Role) => {
      const permissionCount = count ?? record.permissionCount ?? 0;
      return (
        <Tag
          color={permissionCount > 0 ? "green" : "orange"}
          style={{ cursor: "pointer" }}
          title="点击查看权限列表"
          onClick={() => onViewPermissions(record)}
        >
          {permissionCount}
        </Tag>
      );
    },
  },
  {
    title: "创建时间",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 180,
    minWidth: 150,
    render: (text: string) => {
      if (!text) return "-";
      try {
        return new Date(text).toLocaleString();
      } catch {
        return "-";
      }
    },
  },
  {
    title: "操作",
    key: "action",
    width: 100,
    minWidth: 100,
    fixed: "right" as const,
    render: (_: any, record: Role) => {
      const isSystemRole = record.isSystem || record.code === "SUPER-ADMINS";
      return (
        <Space size="small">
          <Tooltip title={isSystemRole ? "系统角色不能编辑" : "编辑角色"}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              disabled={isSystemRole}
              onClick={() => !isSystemRole && onEdit(record)}
            />
          </Tooltip>
          <Tooltip title={isSystemRole ? "系统角色不能删除" : "删除角色"}>
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={isSystemRole}
              onClick={() => !isSystemRole && onDelete(record)}
            />
          </Tooltip>
        </Space>
      );
    },
  },
];

export const createTableButtonsConfig = (
  onRefresh: () => void,
  onCreate: () => void,
  onBatchDelete: () => void,
  selectedCount: number
): TableButtonConfig[] => [
  {
    key: "batch_delete",
    text: `删除选中 (${selectedCount})`,
    // 使用 ContainerPanel 的 danger 映射（type="danger" -> Button type="default" + danger）
    type: "danger" as const,
    icon: React.createElement(DeleteOutlined),
    group: "secondary" as const,
    disabled: selectedCount === 0,
    onClick: onBatchDelete,
  },
  {
    key: "refresh",
    icon: React.createElement(ReloadOutlined),
    onClick: onRefresh,
    group: "primary" as const,
    text: "刷新",
  },
  {
    key: "add",
    type: "primary" as const,
    icon: React.createElement(PlusOutlined),
    onClick: onCreate,
    group: "primary" as const,
    text: "新建",
  },
];
